import {
  FilterBooleanIcon,
  // FilterSurveyIcon,
  FilterDateIcon,
  FilterEmailIcon,
  FilterExternalIdIcon,
  FilterGeoFenceIcon,
  FilterLocationIcon,
  FilterNameIcon,
  FilterNumberIcon,
  FilterPhoneIcon,
  FilterStatusIcon,
  FilterTextIcon,
  FilterTimeIcon,
  FilterUrlIcon,
  FilterVehicleIcon,
  FilterVolumeIcon,
  FilterWeightIcon,
} from '../../../assets/img/filters';
import { PlannerCredentials } from '../../../domain/credentials/PlannerCredentials';
import { GeoFence } from '../../../domain/geoFence/GeoFence';
import { Project } from '../../../domain/project/Project';
import { Supervisor } from '../../../domain/supervisor/Supervisor';
import { VehicleFlat } from '../../../domain/vehicle/VehicleFlat';
import { GeoFenceMapper } from '../../../mappers/geoFence/GeoFenceMapper';
import { SearchFilterTypes } from './filter/SearchFilter';
import { SearchFilterGeojsonOptionProps, SearchFilterGeojsonValue } from './geojson/SearchFilterGeojson';
import { SearchFilterListOptionProps } from './list/SearchFilterList';
import { SearchFilterObjectListOptionProps } from './object_list/SearchFilterObjectList';

interface SearchFilterOption {
  type: SearchFilterTypes;
  field: string;
  intlId: string;
  iconSrc: string;
  isCustomField: boolean;
  listOptions?: SearchFilterListOptionProps[] | SearchFilterObjectListOptionProps[] | SearchFilterGeojsonOptionProps[];
  formatted?: (value: any) => any;
  computed?: (value: any) => any;
}

export const getAvailableOptions = (
  project?: Project,
  userCredentials?: PlannerCredentials,
  geoFences?: GeoFence[],
  projectVehicles?: VehicleFlat[],
  supervisors?: Supervisor[]
): SearchFilterOption[] => {
  if (!project || !userCredentials) {
    return [];
  }

  const availableOption: SearchFilterOption[] = [
    {
      field: `label`,
      type: `string`,
      intlId: `search_filters.filter.label`,
      iconSrc: FilterNameIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `created_at`,
      type: `date`,
      intlId: `search_filters.filter.created_at`,

      iconSrc: FilterDateIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `status`,
      type: `list`,
      intlId: `search_filters.filter.status`,
      iconSrc: FilterStatusIcon,
      isCustomField: false,
      listOptions: [
        {
          value: `pending`,
          intlId: `search_filters.filter.status.pending`,
        },
        {
          value: `completed`,
          intlId: `search_filters.filter.status.completed`,
        },
        {
          value: `incomplete`,
          intlId: `search_filters.filter.status.incomplete`,
        },
        {
          value: `canceled`,
          intlId: `search_filters.filter.status.canceled`,
        },
      ],
    } as SearchFilterOption,

    ...(project.planner.stopFieldsHasSpecificField(`location_details`)
      ? [
          {
            field: `location_details`,
            type: `string`,
            intlId: `search_filters.filter.location_details`,
            iconSrc: FilterLocationIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.planner.stopFieldsHasSpecificField(`comments`)
      ? [
          {
            field: `comments`,
            type: `string`,
            intlId: `search_filters.filter.comments`,
            iconSrc: FilterTextIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    {
      field: `vehicle_id`,
      type: `object_list`,
      intlId: `search_filters.filter.vehicle`,
      iconSrc: FilterVehicleIcon,
      isCustomField: false,
      listOptions: projectVehicles
        ? projectVehicles.map((vehicle) => ({ value: vehicle.id, label: vehicle.label }))
        : [],
    } as SearchFilterOption,

    ...(userCredentials?.subscription.test(`planner.geofences`) && project.geo_fences_enabled
      ? [
          {
            field: `geo_fence`,
            type: `geojson`,
            intlId: `search_filters.filter.geo_fence`,
            iconSrc: FilterGeoFenceIcon,
            isCustomField: false,
            listOptions: geoFences
              ? geoFences.map(
                  (geoFence) => ({ value: GeoFenceMapper.toDTO(geoFence) } as SearchFilterGeojsonOptionProps)
                )
              : [],
            computed: (value: SearchFilterGeojsonValue) => {
              return value.map((geoFence) => geoFence.label).join(`, `);
            },
          } as SearchFilterOption,
        ]
      : []),

    ...(userCredentials?.subscription.test(`supervisors`) && supervisors && supervisors.length > 0
      ? [
          {
            field: `supervisor_id`,
            type: `object_list`,
            intlId: `search_filters.filter.supervisor_id`,
            iconSrc: FilterNameIcon,
            isCustomField: false,
            listOptions: supervisors
              ? supervisors.map((supervisor) => ({ value: supervisor.id, label: supervisor.name }))
              : [],
          } as SearchFilterOption,
        ]
      : []),

    {
      field: `client_external_id`,
      type: `string`,
      intlId: `search_filters.filter.client_external_id`,
      iconSrc: FilterTextIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `estimated_arrival_time`,
      type: `date_time`,
      intlId: `search_filters.filter.estimated_arrival_time`,
      iconSrc: FilterDateIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `estimated_departure_time`,
      type: `date_time`,
      intlId: `search_filters.filter.estimated_departure_time`,
      iconSrc: FilterDateIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `planned_arrival_time`,
      type: `time`,
      intlId: `search_filters.filter.planned_arrival_time`,
      iconSrc: FilterTimeIcon,
      isCustomField: false,
      computed: (value: number) => {
        if (value !== undefined && value !== null) {
          const newValue = value * 60;
          return newValue;
        }
        return null;
      },
      formatted: (value: number) => {
        if (value !== undefined && value !== null) {
          const newValue = value / 60;
          return newValue;
        }
        return null;
      },
    } as SearchFilterOption,

    {
      field: `planned_departure_time`,
      type: `time`,
      intlId: `search_filters.filter.planned_departure_time`,
      iconSrc: FilterTimeIcon,
      isCustomField: false,
      computed: (value: number) => {
        if (value !== undefined && value !== null) {
          const newValue = value * 60;
          return newValue;
        }
        return null;
      },
      formatted: (value: number) => {
        if (value !== undefined && value !== null) {
          const newValue = value / 60;
          return newValue;
        }
        return null;
      },
    } as SearchFilterOption,

    {
      field: `customer_text_field`,
      type: `string`,
      intlId: `search_filters.filter.customer_text_field`,
      iconSrc: FilterTextIcon,
      isCustomField: false,
    } as SearchFilterOption,

    ...(project.drivers.configHasField(`pin`)
      ? [
          {
            field: `pin`,
            type: `string`,
            intlId: `search_filters.filter.pin`,
            iconSrc: FilterTextIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.drivers.configHasField(`stop_duration`)
      ? [
          {
            field: `duration`,
            type: `number`,
            intlId: `search_filters.filter.duration`,
            iconSrc: FilterTimeIcon,
            isCustomField: false,
            formatted: (value: number) => {
              if (value !== undefined && value !== null) {
                const newValue = value * 60;
                return newValue;
              }
              return null;
            },
            computed: (value: number) => {
              if (value !== undefined && value !== null) {
                const newValue = value / 60;
                return newValue;
              }
              return null;
            },
          } as SearchFilterOption,
        ]
      : []),

    ...(project.constraints.weight
      ? [
          {
            field: `weight`,
            type: `number`,
            intlId: `search_filters.filter.weight`,
            iconSrc: FilterWeightIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.constraints.volume
      ? [
          {
            field: `volume`,
            type: `number`,
            intlId: `search_filters.filter.volume`,
            iconSrc: FilterVolumeIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.constraints.cluster
      ? [
          {
            field: `cluster`,
            type: `string`,
            intlId: `search_filters.filter.cluster`,
            iconSrc: FilterTextIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.constraints.reward
      ? [
          {
            field: `reward`,
            type: `number`,
            intlId: `search_filters.filter.reward`,
            iconSrc: FilterNumberIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.constraints.max_delivery_time
      ? [
          {
            field: `max_delivery_time`,
            type: `number`,
            intlId: `search_filters.filter.max_delivery_time`,
            iconSrc: FilterTimeIcon,
            isCustomField: false,
            formatted: (value: number) => {
              if (value !== undefined && value !== null) {
                const newValue = value * 60;
                return newValue;
              }
              return null;
            },
            computed: (value: number) => {
              if (value !== undefined && value !== null) {
                const newValue = value / 60;
                return newValue;
              }
              return null;
            },
          } as SearchFilterOption,
        ]
      : []),

    ...(project.planner.stopFieldsHasSpecificField(`external_id`)
      ? [
          {
            field: `external_id`,
            type: `string`,
            intlId: `search_filters.filter.external_id`,
            iconSrc: FilterExternalIdIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.planner.stopFieldsHasSpecificField(`reference_person`)
      ? [
          {
            field: `reference_person`,
            type: `string`,
            intlId: `search_filters.filter.reference_person`,
            iconSrc: FilterTextIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.planner.stopFieldsHasSpecificField(`reference_person`)
      ? [
          {
            field: `email`,
            type: `string`,
            intlId: `search_filters.filter.email`,
            iconSrc: FilterEmailIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.planner.stopFieldsHasSpecificField(`url`)
      ? [
          {
            field: `url`,
            type: `string`,
            intlId: `search_filters.filter.url`,
            iconSrc: FilterUrlIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    ...(project.planner.stopFieldsHasSpecificField(`phone`)
      ? [
          {
            field: `phone`,
            type: `string`,
            intlId: `search_filters.filter.phone`,
            iconSrc: FilterPhoneIcon,
            isCustomField: false,
          } as SearchFilterOption,
        ]
      : []),

    {
      field: `report_created_at`,
      type: `date`,
      intlId: `search_filters.filter.report_created_at`,
      iconSrc: FilterDateIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `report_comments`,
      type: `string`,
      intlId: `search_filters.filter.report_comments`,
      iconSrc: FilterTextIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `report_signature`,
      type: `boolean`,
      intlId: `search_filters.filter.report_signature`,
      iconSrc: FilterBooleanIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `report_images`,
      type: `boolean`,
      intlId: `search_filters.filter.report_images`,
      iconSrc: FilterBooleanIcon,
      isCustomField: false,
    } as SearchFilterOption,

    {
      field: `report_cancel_reason`,
      type: `list`,
      intlId: `search_filters.filter.report_cancel_reason`,
      iconSrc: FilterTextIcon,
      isCustomField: false,
      listOptions: [
        {
          value: `nobody`,
          intlId: `search_filters.filter.report_cancel_reason.nobody`,
        },
        {
          value: `wrong_address`,
          intlId: `search_filters.filter.report_cancel_reason.wrong_address`,
        },
        {
          value: `missing_information`,
          intlId: `search_filters.filter.report_cancel_reason.missing_information`,
        },
        {
          value: `other`,
          intlId: `search_filters.filter.report_cancel_reason.other`,
        },
      ],
    } as SearchFilterOption,

    // ...(project.drivers.configHasField(`survey`)
    //   ? [
    //       {
    //         field: `survey`,
    //         type: `survey`,
    //         intlId: `search_filters.filter.survey`,
    //         iconSrc: FilterSurveyIcon,
    //         isCustomField: false,
    //       } as SearchFilterOption,
    //     ]
    //   : []),
  ];

  // TODO: CUSTOM FIELDS

  return availableOption;
};
